import type { Report, ReportTemplate, TemplateSection } from '../schemas';
import { parseKeypath } from '../idGenerator';

const ATTENDEES_KEY = 'sec_persone-presenti-al-sopralluogo.fld_partecipanti-sopralluogo';

interface VisitPrefillKeyMap {
  repeatingSections: Set<string>;
  staticKeys: Set<string>;
}

function sectionUsesVisit(section: TemplateSection): boolean {
  if (section.type === 'repeating' && section.repeat?.forEach.startsWith('visit.')) {
    return true;
  }

  return section.fields.some(
    (field) => typeof field.source === 'string' && field.source.includes('visit.')
  );
}

export function collectVisitPrefillKeys(template: ReportTemplate): VisitPrefillKeyMap {
  const repeatingSections = new Set<string>();
  const staticKeys = new Set<string>();

  for (const section of template.sections) {
    if (!sectionUsesVisit(section)) continue;

    if (section.type === 'repeating') {
      repeatingSections.add(section.id);
      continue;
    }

    for (const field of section.fields) {
      if (typeof field.source === 'string' && field.source.includes('visit.')) {
        const key = `${section.id}.${field.id}`;
        if (key !== ATTENDEES_KEY) {
          staticKeys.add(key);
        }
      }
    }
  }

  return { repeatingSections, staticKeys };
}

export function applyVisitPrefillFromPrevious(
  report: Report,
  template: ReportTemplate,
  previousReport: Report
): Report {
  const { repeatingSections, staticKeys } = collectVisitPrefillKeys(template);
  const nextValues = { ...report.data.values };
  const previousValues = previousReport.data.values;

  for (const key of staticKeys) {
    if (key in previousValues && key in nextValues) {
      nextValues[key] = previousValues[key];
    }
  }

  const entries = Object.entries(nextValues);
  for (const [key] of entries) {
    if (!(key in previousValues)) continue;
    const { sectionId } = parseKeypath(key);
    if (!repeatingSections.has(sectionId)) continue;
    nextValues[key] = previousValues[key];
  }

  return {
    ...report,
    data: {
      ...report.data,
      values: nextValues,
    },
  };
}
